import React, { useEffect, useRef, useState } from 'react';

const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  '[role="button"]',
  'input:not([type="hidden"])',
  'textarea',
  'select',
  'label[for]',
  '.cursor-pointer',
  '.cursor-magnetic',
].join(', ');

const MAGNETIC_STRENGTH = 0.18;
const RING_FOLLOW_SPEED = 22;
const MAX_PARTICLES = 14;
const MOTION_SMOOTH_SPEED = 14;

const isFinePointerDevice = () => {
  if (typeof window === 'undefined') return false;
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const noHover = window.matchMedia('(hover: none)').matches;
  return !hasTouch && !coarsePointer && !noHover;
};

const CustomCursor = () => {
  const [enabled] = useState(() => isFinePointerDevice());
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const hoverRef = useRef(false);
  const visibleRef = useRef(false);
  const hoverTargetRef = useRef(null);
  const magneticCenterRef = useRef({ x: 0, y: 0 });
  const mouseRef = useRef({ x: -100, y: -100 });
  const ringPosRef = useRef({ x: -100, y: -100 });
  const prevTargetRef = useRef({ x: -100, y: -100 });
  const motionRef = useRef(0);
  const particlesRef = useRef([]);
  const frameRef = useRef(0);
  const lastFrameTimeRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    document.body.classList.add('custom-cursor-active');

    const dotEl = dotRef.current;
    const ringEl = ringRef.current;
    const canvas = canvasRef.current;
    if (!dotEl || !ringEl || !canvas) return undefined;

    const ctx = canvas.getContext('2d', { alpha: true });
    let canvasWidth = window.innerWidth;
    let canvasHeight = window.innerHeight;

    const resizeCanvas = () => {
      canvasWidth = window.innerWidth;
      canvasHeight = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const updateMagneticCenter = (target) => {
      if (!target) return;
      const rect = target.getBoundingClientRect();
      magneticCenterRef.current = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    };

    resizeCanvas();

    const setVisibility = (isVisible) => {
      visibleRef.current = isVisible;
      const opacity = isVisible ? '1' : '0';
      dotEl.style.opacity = opacity;
      ringEl.style.opacity = opacity;
    };

    const getTargetPoint = () => {
      if (!hoverRef.current) return mouseRef.current;

      const { x, y } = magneticCenterRef.current;
      return {
        x: mouseRef.current.x + (x - mouseRef.current.x) * MAGNETIC_STRENGTH,
        y: mouseRef.current.y + (y - mouseRef.current.y) * MAGNETIC_STRENGTH,
      };
    };

    const spawnParticle = (x, y, vx, vy, intensity) => {
      const particles = particlesRef.current;
      particles.push({
        x,
        y,
        vx: -vx * 0.08 + (Math.random() - 0.5) * 0.8,
        vy: -vy * 0.08 + (Math.random() - 0.5) * 0.8,
        life: 0.35 + intensity * 0.35,
        size: 1.2 + intensity * 1.8,
      });

      if (particles.length > MAX_PARTICLES) {
        particles.splice(0, particles.length - MAX_PARTICLES);
      }
    };

    const drawMotionEffects = (fromX, fromY, toX, toY, intensity) => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      if (intensity > 0.08) {
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX, toY);
        ctx.strokeStyle = `rgba(100, 255, 218, ${intensity * 0.22})`;
        ctx.lineWidth = 1 + intensity * 2.5;
        ctx.lineCap = 'round';
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(toX, toY, 4 + intensity * 8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(100, 255, 218, ${intensity * 0.08})`;
        ctx.fill();
      }

      const particles = particlesRef.current;
      let writeIndex = 0;

      for (let i = 0; i < particles.length; i += 1) {
        const particle = particles[i];
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life *= 0.82;

        if (particle.life <= 0.05) continue;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * particle.life, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(100, 255, 218, ${particle.life * 0.45})`;
        ctx.fill();

        particles[writeIndex] = particle;
        writeIndex += 1;
      }

      particles.length = writeIndex;
    };

    const animate = (timestamp) => {
      const delta = lastFrameTimeRef.current
        ? Math.min((timestamp - lastFrameTimeRef.current) / 1000, 0.032)
        : 0.016;
      lastFrameTimeRef.current = timestamp;
      frameRef.current += 1;

      const target = getTargetPoint();
      const hovering = hoverRef.current;
      const ringEase = 1 - Math.exp(-RING_FOLLOW_SPEED * delta);

      const dx = target.x - prevTargetRef.current.x;
      const dy = target.y - prevTargetRef.current.y;
      const speed = Math.hypot(dx, dy) / Math.max(delta, 0.001);
      const targetMotion = Math.min(speed / 1400, 1);
      const motionEase = 1 - Math.exp(-MOTION_SMOOTH_SPEED * delta);
      motionRef.current += (targetMotion - motionRef.current) * motionEase;

      ringPosRef.current.x += (target.x - ringPosRef.current.x) * ringEase;
      ringPosRef.current.y += (target.y - ringPosRef.current.y) * ringEase;

      const motion = motionRef.current;
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      const dotScale = (hovering ? 1.35 : 1) * (1 + motion * 0.12);
      const stretch = 1 + motion * 0.45;
      const squeeze = 1 - motion * 0.18;
      const ringScale = (hovering ? 1.45 : 1) * (1 + motion * 0.18);

      dotEl.style.transform =
        `translate3d(${target.x}px, ${target.y}px, 0) translate(-50%, -50%) ` +
        `rotate(${motion > 0.05 ? angle : 0}deg) scale(${dotScale * squeeze}, ${dotScale * stretch})`;
      ringEl.style.transform =
        `translate3d(${ringPosRef.current.x}px, ${ringPosRef.current.y}px, 0) translate(-50%, -50%) scale(${ringScale})`;

      const isMoving = motion > 0.06;
      dotEl.dataset.hover = hovering ? 'true' : 'false';
      ringEl.dataset.hover = hovering ? 'true' : 'false';
      dotEl.dataset.moving = isMoving ? 'true' : 'false';
      ringEl.dataset.moving = isMoving ? 'true' : 'false';

      if (visibleRef.current && motion > 0.06) {
        const spawnEvery = motion > 0.35 ? 2 : 3;
        if (frameRef.current % spawnEvery === 0) {
          spawnParticle(target.x, target.y, dx, dy, motion);
        }
      }

      drawMotionEffects(ringPosRef.current.x, ringPosRef.current.y, target.x, target.y, motion);
      prevTargetRef.current = { x: target.x, y: target.y };

      rafRef.current = requestAnimationFrame(animate);
    };

    const handlePointerMove = (event) => {
      mouseRef.current.x = event.clientX;
      mouseRef.current.y = event.clientY;

      if (!visibleRef.current) {
        ringPosRef.current.x = event.clientX;
        ringPosRef.current.y = event.clientY;
        prevTargetRef.current = { x: event.clientX, y: event.clientY };
        setVisibility(true);
      }
    };

    const handlePointerOver = (event) => {
      const target = event.target.closest(INTERACTIVE_SELECTOR);
      if (!target || target.closest('.custom-cursor-root')) return;
      hoverRef.current = true;
      hoverTargetRef.current = target;
      updateMagneticCenter(target);
    };

    const handlePointerOut = (event) => {
      const nextTarget = event.relatedTarget?.closest?.(INTERACTIVE_SELECTOR);
      if (nextTarget) {
        hoverTargetRef.current = nextTarget;
        updateMagneticCenter(nextTarget);
        return;
      }
      hoverRef.current = false;
      hoverTargetRef.current = null;
    };

    const handleWindowLeave = () => setVisibility(false);
    const handleWindowEnter = () => setVisibility(true);

    const handleScroll = () => {
      if (hoverRef.current && hoverTargetRef.current) {
        updateMagneticCenter(hoverTargetRef.current);
      }
    };

    window.addEventListener('resize', resizeCanvas, { passive: true });
    window.addEventListener('mousemove', handlePointerMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    window.addEventListener('mouseover', handlePointerOver);
    window.addEventListener('mouseout', handlePointerOut);
    document.addEventListener('mouseleave', handleWindowLeave);
    document.addEventListener('mouseenter', handleWindowEnter);

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      document.body.classList.remove('custom-cursor-active');
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('mouseover', handlePointerOver);
      window.removeEventListener('mouseout', handlePointerOut);
      document.removeEventListener('mouseleave', handleWindowLeave);
      document.removeEventListener('mouseenter', handleWindowEnter);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="custom-cursor-root" aria-hidden="true">
      <canvas ref={canvasRef} className="custom-cursor-canvas" />
      <div ref={ringRef} className="custom-cursor-ring" />
      <div ref={dotRef} className="custom-cursor-dot" />
    </div>
  );
};

export default CustomCursor;
